import React, { useState } from "react";
import {
  Typography,
  FormControl,
  Button,
  Select,
  Checkbox,
  ListItemIcon,
  FormControlLabel,
  CircularProgress,
  Tooltip,
  Box,
  Paper,
  MenuItem,
  ListItemText,
  Divider,
  Popover,
  TextField,
  List,
  ListItem,
  Modal,
} from "@material-ui/core";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { makeStyles, useTheme } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  barContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    gap: theme.spacing(1.5),
    fontFamily: "Raleway, sans-serif",
  },
  modeSelect: {
    maxWidth: "max-content",
    backgroundColor: "#2a2a2a",
    color: "white",
    borderRadius: 8,
    "& .MuiSelect-select": {
      padding: "10px 14px",
      display: "flex",
      alignItems: "center",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "& .MuiSelect-icon": {
      color: "#FFC629",
    },
    "& .MuiSelect-root": {
      width: "max-content",
      marginRight: theme.spacing(2),
      fontSize: "0.9rem",
      fontFamily: "Poppins, sans-serif",
    },
  },
  controlInput: {
    minWidth: 250,
    backgroundColor: "#2a2a2a",
    borderRadius: 8,
    "& .MuiOutlinedInput-root": {
      // height: 40,
      color: "white",
      "& fieldset": { border: "none" },
    },
    "& .MuiInputBase-input": {
      padding: "10px 14px",
      textAlign: "left",
    },
    "& .MuiSvgIcon-root": {
      color: "#FFC629",
    },
  },
  checkbox: {
    color: "#FFC629",
    "&.Mui-checked": {
      color: "#FFC629",
    },
  },
  button: {
    backgroundColor: "#FFC629",
    color: "#000000",
    "&:hover": {
      backgroundColor: "#FFC629",
      color: "#000000",
    },
    borderRadius: "8px",
    padding: theme.spacing(1, 2),
    fontSize: "0.9rem",
    fontWeight: "bold",
    textTransform: "none",
    fontFamily: "Raleway, sans-serif",
    height: 40,
  },
  dropzone: {
    border: "2px dashed #444",
    borderRadius: "12px",
    padding: theme.spacing(4),
    textAlign: "center",
    cursor: "pointer",
    transition: "border-color 0.3s, background-color 0.3s",
    backgroundColor: "#2a2a2a",
    "&:hover": {
      borderColor: "#FACC15",
    },
  },
  modal: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: 500,
    backgroundColor: "#202020",
    borderRadius: "14px",
    padding: theme.spacing(4),
    outline: "none",
  },
  modalTitle: {
    color: "white",
    fontSize: "1.2rem",
    marginBottom: theme.spacing(2),
    fontFamily: "Raleway, sans-serif",
  },
  listItemCollectionText: {
    fontFamily: "Poppins, sans-serif",
    color: "black",
    fontWeight: "lighter",
    fontSize: "0.9rem",
  },
  fileContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(2),
    color: "white",
  },
  removeIcon: {
    cursor: "pointer",
    color: "#888",
    "&:hover": {
      color: "#ffffff",
    },
  },
  selectMenuItem: {
    fontFamily: "Poppins, sans-serif",
    fontSize: "0.9rem",
  },
}));

const MainChatSelectionPanel = ({
  selectionType,
  handleSelectionTypeChange,
  chatId,
  selectedCollections,
  handleCollectionChange,
  collections,
  selectedDatabase,
  handleDatabaseChange,
  databases,
  loading,
  availableCollections,
  setSelectedCollections,
  includeAnalysis,
  setIncludeAnalysis,
  csvGetRootProps,
  csvGetInputProps,
  csvFile,
  handleCsvUpload,
  handleRemoveCsvFile,
  isUploaded,
}) => {
  const classes = useStyles();
  const theme = useTheme();

  // Custom dropdown state
  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverWidth, setPopoverWidth] = useState(0);
  const collectionsDropdownOpen = Boolean(anchorEl);

  // Modal state for CSV
  const [csvModalOpen, setCsvModalOpen] = useState(false);

  const handleOpenDropdown = (event) => {
    if (!chatId) {
      setAnchorEl(event.currentTarget);
      setPopoverWidth(event.currentTarget.offsetWidth);
    }
  };

  const handleCloseDropdown = () => {
    setAnchorEl(null);
  };

  const handleToggleCollection = (collection) => {
    const currentIndex = selectedCollections.findIndex(
      (c) => c.id === collection.id,
    );
    const newSelected = [...selectedCollections];

    if (currentIndex === -1) {
      if (selectedCollections.length >= 5) {
        return; // Limit to 5 collections
      }
      newSelected.push(collection);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    // Create a synthetic event to match the expected shape
    const syntheticEvent = {
      target: { value: newSelected },
    };
    handleCollectionChange(syntheticEvent);
  };

  const handleModeChange = (event) => {
    // Adapter for RadioGroup change handler which expects event.target.value
    handleSelectionTypeChange(event);
  };

  return (
    <Box className={classes.barContainer}>
      {/* Mode Selector */}
      <Box style={{ display: "flex", alignItems: "center", gap: 8 }} data-tour="data-source-select">
        <Typography
          style={{
            color: "#aaa",
            fontSize: "1rem",
            whiteSpace: "nowrap",
            fontFamily: "Poppins, sans-serif",
          }}
        >
          Data source:{" "}
        </Typography>
        <Select
          value={selectionType}
          onChange={handleModeChange}
          variant="outlined"
          className={classes.modeSelect}
          MenuProps={{
            getContentAnchorEl: null, // Disables default centering
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "right",
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            PaperProps: {
              style: {
                fontFamily: "Poppins, sans-serif",
                backgroundColor: "white",
                color: "#000000",
                marginTop: 8,
              },
            },
          }}
        >
          <MenuItem value="collections" className={classes.selectMenuItem}>
            Collections
          </MenuItem>
          <MenuItem value="databases" className={classes.selectMenuItem}>
            <Tooltip title="This feature is in beta — functionality may be limited or change" placement="right">
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                Databases
                <span style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: "#FFC629",
                  backgroundColor: "rgba(255,198,41,0.12)",
                  borderRadius: 4,
                  padding: "1px 6px",
                  lineHeight: "16px",
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}>Beta</span>
              </Box>
            </Tooltip>
          </MenuItem>
          <MenuItem value="csv" className={classes.selectMenuItem}>
            <Tooltip title="This feature is in beta — functionality may be limited or change" placement="right">
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                Spreadsheet
                <span style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: "#FFC629",
                  backgroundColor: "rgba(255,198,41,0.12)",
                  borderRadius: 4,
                  padding: "1px 6px",
                  lineHeight: "16px",
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}>Beta</span>
              </Box>
            </Tooltip>
          </MenuItem>
        </Select>
      </Box>

      {/* Conditional Inputs based on Mode */}
      {selectionType === "collections" && (
        <Tooltip
          title={
            chatId ? "Start a new chat to select a different collection" : ""
          }
          PopperProps={{ style: { zIndex: 1200 } }}
        >
          <div style={{ flex: 1, maxWidth: 300 }}>
            {/* Custom trigger field */}
            <TextField
              variant="outlined"
              value={
                selectedCollections.length > 0
                  ? `${selectedCollections.length} Selected`
                  : ""
              }
              placeholder="Select collections"
              onClick={handleOpenDropdown}
              disabled={!!chatId}
              fullWidth
              className={classes.controlInput}
              InputProps={{
                readOnly: true,
                endAdornment: <ArrowDropDownIcon />,
                style: {
                  cursor: chatId ? "not-allowed" : "pointer",
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "0.9rem",
                },
              }}
            />

            {/* Popover for Collections */}
            <Popover
              open={collectionsDropdownOpen}
              anchorEl={anchorEl}
              onClose={handleCloseDropdown}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              PaperProps={{
                style: {
                  width: popoverWidth,
                  marginTop: 8,
                },
              }}
            >
              <Box style={{ display: "flex", flexDirection: "column" }}>
                <Box style={{ padding: "8px 16px 0px 16px" }}>
                  <Typography
                    variant="caption"
                    style={{ fontFamily: "Poppins, sans-serif", color: "#666" }}
                  >
                    Select up to 5 collections.
                  </Typography>
                </Box>
                {/* Collection list */}
                {/* Collection list */}
                <List
                  style={{
                    padding: 0,
                    overflow: "auto",
                    maxHeight: collections.length > 6 ? 320 : "initial",
                  }}
                >
                  {collections.length === 0 ? (
                    <ListItem disabled>
                      <ListItemText
                        primary="No collections available"
                        secondary="Go to Collection Management to add one."
                        primaryTypographyProps={{
                          style: { fontFamily: "Poppins, sans-serif" },
                        }}
                      />
                    </ListItem>
                  ) : (
                    <>
                      {/* Helper to render items */}
                      {(() => {
                        const renderCollectionItem = (collection) => {
                          const isSelected =
                            selectedCollections.findIndex(
                              (c) => c.id === collection.id,
                            ) > -1;
                          const limitReached = selectedCollections.length >= 5;

                          return (
                            <ListItem
                              key={collection.id}
                              button
                              onClick={() => handleToggleCollection(collection)}
                              disabled={limitReached && !isSelected}
                              style={{ padding: "1px 12px" }}
                            >
                              <ListItemIcon style={{ minWidth: 36 }}>
                                <Checkbox
                                  checked={isSelected}
                                  disabled={limitReached && !isSelected}
                                  className={classes.checkbox}
                                  size="small"
                                  tabIndex={-1}
                                  disableRipple
                                />
                              </ListItemIcon>
                              <ListItemText>
                                <Typography
                                  className={classes.listItemCollectionText}
                                >
                                  {collection.name}
                                </Typography>
                              </ListItemText>
                            </ListItem>
                          );
                        };

                        const publicCols = collections.filter(
                          (c) => c.isPublic,
                        );
                        const privateCols = collections.filter(
                          (c) => !c.isPublic,
                        );

                        return (
                          <>
                            {/* Public Section */}
                            {publicCols.length > 0 && (
                              <>
                                <Box
                                  style={{
                                    padding: "8px 16px 4px",
                                    backgroundColor: "#f5f5f5",
                                  }}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    style={{
                                      fontFamily: "Poppins, sans-serif",
                                      fontWeight: "bold",
                                      color: "#666",
                                    }}
                                  >
                                    Public
                                  </Typography>
                                </Box>
                                {publicCols.map(renderCollectionItem)}
                              </>
                            )}

                            {/* Private Section */}
                            {privateCols.length > 0 && (
                              <>
                                <Box
                                  style={{
                                    padding: "8px 16px 4px",
                                    backgroundColor: "#f5f5f5",
                                  }}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    style={{
                                      fontFamily: "Poppins, sans-serif",
                                      fontWeight: "bold",
                                      color: "#666",
                                    }}
                                  >
                                    Private
                                  </Typography>
                                </Box>
                                {privateCols.map(renderCollectionItem)}
                              </>
                            )}
                          </>
                        );
                      })()}
                    </>
                  )}
                </List>

                {/* Done button */}
                {collections.length > 0 && (
                  <>
                    <Divider />
                    <Box style={{ padding: 12 }}>
                      <Button
                        fullWidth
                        onClick={handleCloseDropdown}
                        style={{
                          backgroundColor: "#FFC629",
                          color: "#000000",
                          borderRadius: 8,
                          padding: "8px 16px",
                          fontWeight: "bold",
                          textTransform: "none",
                          fontFamily: "Poppins, sans-serif",
                        }}
                      >
                        Done
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            </Popover>
          </div>
        </Tooltip>
      )}

      {selectionType === "databases" && (
        <>
          <Tooltip
            title={chatId ? "Start a new chat to select a database" : ""}
          >
            <div style={{ maxWidth: 300 }}>
              <Select
                value={selectedDatabase}
                onChange={handleDatabaseChange}
                disabled={!!chatId}
                displayEmpty
                variant="outlined"
                className={classes.controlInput}
                MenuProps={{
                  getContentAnchorEl: null, // Disables default centering
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "right",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "right",
                  },
                  PaperProps: {
                    style: {
                      backgroundColor: "white",
                      color: "#000000",
                      marginTop: 8,
                    },
                  },
                }}
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <span
                        style={{
                          color: "#aaa",
                          fontFamily: "Poppins, sans-serif",
                          fontSize: "0.9rem",
                        }}
                      >
                        Select Database
                      </span>
                    );
                  }
                  return (
                    <Typography
                      style={{
                        color: "white",
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "0.9rem",
                        textAlign: "left",
                      }}
                    >
                      {selected.name}
                    </Typography>
                  );
                }}
              >
                {databases.length === 0 ? (
                  <MenuItem disabled className={classes.selectMenuItem}>
                    No databases available
                  </MenuItem>
                ) : (
                  databases.map((db) => (
                    <MenuItem
                      key={db.id}
                      value={db}
                      className={classes.selectMenuItem}
                    >
                      {db.name} | {db.collectionType}
                    </MenuItem>
                  ))
                )}
              </Select>
            </div>
          </Tooltip>

          {loading && (
            <CircularProgress size={20} style={{ color: "#FFC629" }} />
          )}

          {selectedDatabase && !loading && (
            <div style={{ maxWidth: 300 }}>
              <Select
                multiple
                displayEmpty
                value={selectedCollections}
                onChange={(e) => setSelectedCollections(e.target.value)}
                variant="outlined"
                className={classes.controlInput}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return (
                      <span
                        style={{
                          color: "#aaa",
                          fontFamily: "Poppins, sans-serif",
                          fontSize: "0.9rem",
                          textAlign: "left",
                        }}
                      >
                        Select Tables (Optional)
                      </span>
                    );
                  }
                  return (
                    <Typography
                      style={{
                        color: "white",
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "0.9rem",
                        textAlign: "left",
                      }}
                    >
                      {selected.length} Selected
                    </Typography>
                  );
                }}
                MenuProps={{
                  getContentAnchorEl: null, // Disables default centering
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "right",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "right",
                  },
                  PaperProps: {
                    style: {
                      backgroundColor: "white",
                      color: "#000000",
                      marginTop: 8,
                    },
                  },
                }}
              >
                {availableCollections.length === 0 ? (
                  <MenuItem disabled className={classes.selectMenuItem}>
                    No tables available
                  </MenuItem>
                ) : (
                  availableCollections.map((collection) => (
                    <MenuItem
                      key={collection}
                      value={collection}
                      className={classes.selectMenuItem}
                      style={{ padding: "1px 12px" }}
                    >
                      <ListItemIcon style={{ minWidth: 36 }}>
                        <Checkbox
                          checked={selectedCollections.indexOf(collection) > -1}
                          className={classes.checkbox}
                          size="small"
                          disableRipple
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            className={classes.listItemCollectionText}
                          >
                            {collection}
                          </Typography>
                        }
                      />
                    </MenuItem>
                  ))
                )}
              </Select>
            </div>
          )}

          <Tooltip title="Analyse query results and explain key findings using AI">
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeAnalysis}
                  onChange={(e) => setIncludeAnalysis(e.target.checked)}
                  className={classes.checkbox}
                  size="small"
                />
              }
              label={
                <Typography
                  style={{
                    color: "white",
                    fontFamily: "Raleway, sans-serif",
                    fontSize: "0.9rem",
                  }}
                >
                  Analysis
                </Typography>
              }
            />
          </Tooltip>
        </>
      )}

      {selectionType === "csv" && (
        <Box style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {!isUploaded && (
            <Button
              className={classes.button}
              onClick={() => setCsvModalOpen(true)}
            >
              Upload CSV/XLSX to begin
            </Button>
          )}

          {isUploaded && (
            <Box className={classes.fileContainer}>
              <Typography style={{ color: "white", fontSize: "0.9rem" }}>
                Spreadsheet: {csvFile ? csvFile.name : "Uploaded File"}
              </Typography>
              <Tooltip title="Remove file">
                <ClearIcon
                  fontSize="small"
                  className={classes.removeIcon}
                  onClick={handleRemoveCsvFile}
                />
              </Tooltip>
            </Box>
          )}

          {/* CSV Upload Modal */}
          <Modal open={csvModalOpen} onClose={() => setCsvModalOpen(false)}>
            <Box className={classes.modal}>
              <Box className={classes.dropzone} {...csvGetRootProps()}>
                <input {...csvGetInputProps()} />
                {csvFile ? (
                  <Box>
                    <CloudUploadOutlinedIcon
                      style={{
                        fontSize: 48,
                        color: "#FACC15",
                        marginBottom: 16,
                      }}
                    />
                    <Box
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8, // Space between text and icon
                      }}
                    >
                      <Typography
                        style={{
                          color: "white",
                          fontFamily: "Poppins, sans-serif",
                        }}
                      >
                        <span style={{ fontWeight: "bold" }}>Selected: </span>{csvFile.name}
                      </Typography>

                      <Tooltip title="Remove file">
                        <ClearIcon
                          fontSize="small"
                          className={classes.removeIcon}
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent dropzone click
                            handleRemoveCsvFile();
                          }}
                          style={{
                            color: "#FFC629",
                            zIndex: 10,
                          }}
                        />
                      </Tooltip>
                    </Box>
                    
                    <Typography
                      variant="body2"
                      style={{ color: "#888", marginTop: 8 }}
                    >
                      Click or drag to replace
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <CloudUploadOutlinedIcon
                      style={{
                        fontSize: 48,
                        color: "#FACC15",
                        marginBottom: 16,
                      }}
                    />
                    <Typography
                      style={{
                        color: "white",
                        textAlign: "center",
                        fontFamily: "Poppins, sans-serif",
                      }}
                    >
                      Drag & drop a file here
                    </Typography>
                    <Typography
                      variant="body2"
                      style={{
                        color: "#888",
                        textAlign: "center",
                        fontFamily: "Poppins, sans-serif",
                      }}
                    >
                      CSV and XLSX supported.
                    </Typography>
                  </>
                )}
              </Box>

              <Box
                style={{
                  display: "flex",
                  gap: 12,
                  marginTop: 24,
                  justifyContent: "center",
                }}
              >
                <Button
                  onClick={async (e) => {
                    const success = await handleCsvUpload(e);
                    if (success) {
                      setCsvModalOpen(false);
                    }
                  }}
                  disabled={loading || !csvFile}
                  className={classes.button}
                >
                  {loading ? "Uploading..." : "Upload"}
                </Button>
                <Button
                  onClick={() => setCsvModalOpen(false)}
                  style={{
                    color: "white",
                    borderColor: "#444",
                    borderRadius: 8,
                  }}
                  variant="outlined"
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          </Modal>
        </Box>
      )}
    </Box>
  );
};

export default MainChatSelectionPanel;
